/**
 * Angulos medidos na foto de perfil (fotogrametria, Fase 1 do projeto).
 *
 * A pessoa marca, sobre a foto, os marcadores M1-M18 colados no animal e alguns
 * pontos de referencia (a linha do solo e a parede de cada casco); daqui saem os
 * 11 angulos do protocolo, pela definicao de pontos de cada um (`points` em
 * traits.v1.ts). O valor medido no campo com goniometro e o da foto sao
 * comparados depois (Bland-Altman): e isso que valida — ou nao — o metodo.
 *
 * Coordenadas da imagem, com y para BAIXO, normalizadas (0 a 1) pela largura e
 * altura da foto: a marcacao nao depende da resolucao da imagem. Como largura e
 * altura sao normalizadas por medidas diferentes, as funcoes recebem a proporcao
 * da foto para calcular angulos em pixels de verdade.
 */

export interface Ponto {
  x: number;
  y: number;
}

export type PontosDaFoto = Readonly<Record<string, Ponto | undefined>>;

/** Pontos de referencia marcados alem dos marcadores M1-M18. */
export const PONTOS_DE_REFERENCIA = [
  { codigo: 'SOLO_1', rotulo: 'Linha do solo — ponto 1' },
  { codigo: 'SOLO_2', rotulo: 'Linha do solo — ponto 2' },
  { codigo: 'CASCO_A_TOPO', rotulo: 'Casco anterior — topo da parede (coroa)' },
  { codigo: 'CASCO_A_PINCA', rotulo: 'Casco anterior — ponta da pinça' },
  { codigo: 'CASCO_P_TOPO', rotulo: 'Casco posterior — topo da parede (coroa)' },
  { codigo: 'CASCO_P_PINCA', rotulo: 'Casco posterior — ponta da pinça' },
] as const;

/** A parede dorsal de cada casco, pelos dois pontos marcados. */
const PAREDE_DO_CASCO: Record<string, readonly [string, string]> = {
  AN_CASA: ['CASCO_A_TOPO', 'CASCO_A_PINCA'],
  AN_CASP: ['CASCO_P_TOPO', 'CASCO_P_PINCA'],
};

const graus = (rad: number) => (rad * 180) / Math.PI;

/** Em pixels "quadrados": x escalado pela proporcao largura/altura da foto. */
const emPixels = (p: Ponto, proporcao: number): Ponto => ({ x: p.x * proporcao, y: p.y });

/** Angulo no vertice `v` entre os raios v→a e v→b, de 0 a 180 graus. */
export function anguloNoVertice(a: Ponto, v: Ponto, b: Ponto): number {
  const u = { x: a.x - v.x, y: a.y - v.y };
  const w = { x: b.x - v.x, y: b.y - v.y };
  const cos = (u.x * w.x + u.y * w.y) / (Math.hypot(u.x, u.y) * Math.hypot(w.x, w.y));
  return graus(Math.acos(Math.max(-1, Math.min(1, cos))));
}

/**
 * Inclinacao da reta a→b em relacao a horizontal de referencia (a linha do
 * solo, quando marcada; senao a horizontal da foto), de 0 a 90 graus.
 */
export function inclinacao(a: Ponto, b: Ponto, referencia: readonly [Ponto, Ponto] | null): number {
  const angulo = (p: Ponto, q: Ponto) => Math.atan2(q.y - p.y, q.x - p.x);
  const base = referencia ? angulo(referencia[0], referencia[1]) : 0;
  const d = Math.abs(graus(angulo(a, b) - base)) % 180;
  return d > 90 ? 180 - d : d;
}

export interface AnguloDoProtocolo {
  code: string;
  points?: readonly string[] | undefined;
  decimals: number;
}

/**
 * Os angulos do protocolo a partir dos pontos marcados. `null` quando falta
 * algum ponto daquele angulo. `proporcao` = largura / altura da foto.
 */
export function angulosDaFoto(
  pontos: PontosDaFoto,
  angulos: readonly AnguloDoProtocolo[],
  proporcao: number,
): Record<string, number | null> {
  const p = (codigo: string) => {
    const ponto = pontos[codigo];
    return ponto ? emPixels(ponto, proporcao) : undefined;
  };
  const s1 = p('SOLO_1');
  const s2 = p('SOLO_2');
  const solo = s1 && s2 ? ([s1, s2] as const) : null;
  const arredondar = (v: number, casas: number) => Number(v.toFixed(casas));

  return Object.fromEntries(
    angulos.map((t) => {
      const pts = t.points ?? [];
      let valor: number | null = null;
      if (pts.includes('PAREDE_DORSAL')) {
        const [topo, pinca] = (PAREDE_DO_CASCO[t.code] ?? []).map(p);
        if (topo && pinca) valor = inclinacao(topo, pinca, solo);
      } else if (pts[2] === 'HORIZONTAL') {
        const [a, b] = [p(pts[0]!), p(pts[1]!)];
        if (a && b) valor = inclinacao(a, b, solo);
      } else if (pts.length === 3) {
        const [a, v, b] = pts.map(p);
        if (a && v && b) valor = anguloNoVertice(a, v, b);
      }
      return [t.code, valor == null ? null : arredondar(valor, t.decimals)];
    }),
  );
}

/** Todos os codigos de ponto que os angulos pedem, na ordem de marcacao. */
export function pontosNecessarios(angulos: readonly AnguloDoProtocolo[], marcadores: readonly string[]): string[] {
  const usados = new Set(angulos.flatMap((t) => t.points ?? []));
  return [
    ...marcadores.filter((m) => usados.has(m)),
    ...(usados.has('HORIZONTAL') || usados.has('SOLO') ? ['SOLO_1', 'SOLO_2'] : []),
    ...(usados.has('PAREDE_DORSAL') ? ['CASCO_A_TOPO', 'CASCO_A_PINCA', 'CASCO_P_TOPO', 'CASCO_P_PINCA'] : []),
  ];
}
